import { Controller } from "@hotwired/stimulus"

// Main controller for the PDF signing page
// Handles PDF rendering, signature field overlays, and signature capture
export default class extends Controller {
  static targets = [
    "container",
    "pagesContainer",
    "progressText",
    "remainingCount",
    "nextButton",
    "finishButton",
    "signatureModal",
    "drawTab",
    "typeTab",
    "drawMode",
    "typeMode",
    "signaturePad",
    "typeInput",
    "typePreview",
    "insertButton",
    "intentModal",
    "agreeButton",
    "loadingOverlay"
  ]

  static values = {
    pdfUrl: String,
    positions: Array,
    existingSignatures: Object,
    currentPersonId: String, // Can be a number or "agent"
    currentPersonName: String,
    submitUrl: String,
    viewOnly: { type: Boolean, default: false },
    isAgent: { type: Boolean, default: false }
  }

  async connect() {
    this.signatureData = null
    this.signatureMethod = 'draw'
    this.signaturePadInstance = null
    this.currentFieldPersonId = null
    this.agentName = this.currentPersonNameValue // Editable agent name
    this.pdfScale = 1 // Will be set during PDF rendering

    await this.loadPdf()

    // Only initialize signing functionality if not in view-only mode
    if (!this.viewOnlyValue) {
      this.initSignaturePad()
      this.updateProgress()
    }
  }

  async loadPdf() {
    try {
      // Load PDF.js from CDN to avoid import.meta issues with esbuild
      if (!window.pdfjsLib) {
        await this.loadPdfjsFromCdn()
      }
      const pdfjsLib = window.pdfjsLib
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'

      // Fetch PDF
      const response = await fetch(this.pdfUrlValue)
      const arrayBuffer = await response.arrayBuffer()

      // Load PDF document
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      this.pdf = pdf
      this.totalPages = pdf.numPages

      // Clear loading state
      this.pagesContainerTarget.innerHTML = ''

      // Render all pages
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        await this.renderPage(pageNum)
      }
    } catch (error) {
      console.error('Error loading PDF:', error)
      this.pagesContainerTarget.innerHTML = `
        <div class="text-center text-white py-20">
          <p class="text-red-400">Failed to load document. Please refresh and try again.</p>
        </div>
      `
    }
  }

  async renderPage(pageNum) {
    const page = await this.pdf.getPage(pageNum)

    // Calculate responsive scale based on container width
    const scale = this.calculateScale(page)
    this.pdfScale = scale // Store for overlay font scaling
    const viewport = page.getViewport({ scale })

    // Account for high-DPI displays
    const pixelRatio = window.devicePixelRatio || 1

    // Create page wrapper
    const wrapper = document.createElement('div')
    wrapper.className = 'pdf-page-wrapper'

    // Create page container (for positioning overlays)
    const pageContainer = document.createElement('div')
    pageContainer.className = 'pdf-page'
    pageContainer.style.width = `${viewport.width}px`
    pageContainer.style.height = `${viewport.height}px`
    pageContainer.dataset.pageNumber = pageNum

    // Create canvas with high-DPI support
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    // Set actual size in memory (scaled for retina)
    canvas.width = viewport.width * pixelRatio
    canvas.height = viewport.height * pixelRatio

    // Set display size (CSS)
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`

    // Scale context to match pixel ratio
    context.scale(pixelRatio, pixelRatio)

    // Render PDF page
    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise

    pageContainer.appendChild(canvas)

    // Add signature field overlays for this page
    this.addSignatureOverlays(pageContainer, pageNum, viewport)

    wrapper.appendChild(pageContainer)
    this.pagesContainerTarget.appendChild(wrapper)
  }

  calculateScale(page) {
    // Get the container width (minus padding)
    const containerWidth = this.pagesContainerTarget.clientWidth - 32 // 16px padding each side

    // Get the PDF page's natural width at scale 1
    const naturalViewport = page.getViewport({ scale: 1 })
    const naturalWidth = naturalViewport.width

    // Calculate scale to fit width, with a max of 1.5 for larger screens
    const fitScale = containerWidth / naturalWidth
    return Math.min(fitScale, 1.5)
  }

  addSignatureOverlays(pageContainer, pageNum, viewport) {
    const pagePositions = this.positionsValue.filter(p => p.pageNumber === pageNum)

    pagePositions.forEach(position => {
      // Show signature and date fields for everyone
      // Show name fields only for agents (tenant names are pre-filled in PDF)
      if (position.fieldType === 'name') {
        if (!(this.isAgentValue && position.signerType === 'agent')) return
      } else if (position.fieldType !== 'signature' && position.fieldType !== 'date') {
        return
      }

      const overlay = document.createElement('div')
      overlay.className = 'signature-overlay'
      overlay.dataset.personId = position.personId
      overlay.dataset.signerType = position.signerType
      overlay.dataset.signerIndex = position.signerIndex
      overlay.dataset.fieldType = position.fieldType

      // Position as percentage of page dimensions
      overlay.style.left = `${position.xPercent}%`
      overlay.style.top = `${position.yPercent}%`
      overlay.style.width = `${position.widthPercent}%`
      overlay.style.height = `${position.heightPercent}%`

      // Check if this is the current user's field
      // For agents, match signerType === 'agent'; for tenants, match personId
      const isCurrentUser = this.isAgentValue
        ? position.signerType === 'agent'
        : String(position.personId) === String(this.currentPersonIdValue)
      const existingSig = this.existingSignaturesValue[position.personId]

      if (position.fieldType === 'name') {
        // Name field for agent - show pre-filled and editable
        if (isCurrentUser && !this.viewOnlyValue) {
          overlay.classList.add('current-user', 'name-field')
          // Calculate font size based on PDF scale (source is ~10pt = 13.33px)
          const scaledFontSize = Math.round(10 * this.pdfScale)
          overlay.dataset.fontSize = scaledFontSize // Store for later use
          this.renderNameField(overlay, scaledFontSize, this.agentName)
          overlay.style.cursor = 'text'
          overlay.addEventListener('click', (e) => this.editAgentName(e, overlay, scaledFontSize))
        }
      } else if (position.fieldType === 'date') {
        // Date field - show date if signed
        if (existingSig) {
          overlay.classList.add('signed')
          // Mark as other-signer if this isn't the current user's field
          if (!isCurrentUser) {
            overlay.classList.add('other-signer')
          }
          overlay.innerHTML = `<span class="date-text">${this.formatDate(existingSig.signed_at)}</span>`
        } else if (isCurrentUser && !this.viewOnlyValue) {
          // Agent's unsigned date field - hide until signed
          overlay.style.display = 'none'
        } else {
          // Hide date overlay until signed
          overlay.style.display = 'none'
        }
      } else if (position.fieldType === 'signature') {
        if (existingSig) {
          // Already signed - show signature image
          overlay.classList.add('signed')
          // Mark as other-signer if this isn't the current user's field
          if (!isCurrentUser) {
            overlay.classList.add('other-signer')
          }
          const img = document.createElement('img')
          img.src = existingSig.image_url
          img.alt = 'Signature'
          overlay.appendChild(img)
        } else if (isCurrentUser && !this.viewOnlyValue) {
          // Current user's unsigned field (only interactive if not view-only)
          overlay.classList.add('current-user')
          overlay.innerHTML = '<span class="signature-overlay-label">Click to sign</span>'
          overlay.addEventListener('click', () => this.openSignatureModal(position))
        } else {
          // Other user's unsigned field or view-only mode
          overlay.classList.add('pending')
          overlay.innerHTML = '<span class="signature-overlay-label">Pending</span>'
        }
      }

      pageContainer.appendChild(overlay)
    })
  }

  formatDate(isoString) {
    if (!isoString) return ''
    const date = new Date(isoString)
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  }

  initSignaturePad() {
    if (!this.hasSignaturePadTarget) return

    // Dynamically import signature_pad
    import('signature_pad').then(({ default: SignaturePad }) => {
      const canvas = this.signaturePadTarget
      this.signaturePadInstance = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)'
      })

      // Resize canvas to match container
      this.resizeSignaturePad()
      window.addEventListener('resize', () => this.resizeSignaturePad())
    })
  }

  resizeSignaturePad() {
    if (!this.signaturePadInstance || !this.hasSignaturePadTarget) return

    const canvas = this.signaturePadTarget
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    const rect = canvas.getBoundingClientRect()

    canvas.width = rect.width * ratio
    canvas.height = 200 * ratio
    canvas.getContext('2d').scale(ratio, ratio)

    this.signaturePadInstance.clear()
  }

  openSignatureModal(position) {
    this.currentFieldPersonId = position.personId
    this.signatureModalTarget.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')

    // Reset to draw mode
    this.switchToDrawMode()

    // Clear any previous signature
    this.clearSignature()
  }

  closeSignatureModal() {
    this.signatureModalTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
    this.currentFieldPersonId = null
  }

  switchToDrawMode() {
    this.signatureMethod = 'draw'
    this.drawTabTarget.classList.remove('text-gray-500', 'border-transparent')
    this.drawTabTarget.classList.add('text-gray-900', 'border-gray-900')
    this.typeTabTarget.classList.add('text-gray-500', 'border-transparent')
    this.typeTabTarget.classList.remove('text-gray-900', 'border-gray-900')
    this.drawModeTarget.classList.remove('hidden')
    this.typeModeTarget.classList.add('hidden')

    setTimeout(() => this.resizeSignaturePad(), 100)
  }

  switchToTypeMode() {
    this.signatureMethod = 'type'
    this.typeTabTarget.classList.remove('text-gray-500', 'border-transparent')
    this.typeTabTarget.classList.add('text-gray-900', 'border-gray-900')
    this.drawTabTarget.classList.add('text-gray-500', 'border-transparent')
    this.drawTabTarget.classList.remove('text-gray-900', 'border-gray-900')
    this.typeModeTarget.classList.remove('hidden')
    this.drawModeTarget.classList.add('hidden')
  }

  clearSignature() {
    if (this.signaturePadInstance) {
      this.signaturePadInstance.clear()
    }
    if (this.hasTypeInputTarget) {
      this.typeInputTarget.value = ''
    }
    if (this.hasTypePreviewTarget) {
      this.typePreviewTarget.innerHTML = '<span class="text-gray-300">Your signature</span>'
    }
    this.signatureData = null
  }

  updateTypePreview() {
    if (!this.hasTypeInputTarget || !this.hasTypePreviewTarget) return

    const name = this.typeInputTarget.value.trim()
    if (name) {
      this.typePreviewTarget.innerHTML = `<span class="text-gray-900">${this.escapeHtml(name)}</span>`
    } else {
      this.typePreviewTarget.innerHTML = '<span class="text-gray-300">Your signature</span>'
    }
  }

  async insertSignature() {
    if (this.signatureMethod === 'draw') {
      if (!this.signaturePadInstance || this.signaturePadInstance.isEmpty()) {
        alert('Please draw your signature')
        return
      }
      // Get the trimmed signature (removes whitespace)
      this.signatureData = this.trimSignatureCanvas(this.signaturePadTarget)
    } else {
      // Use the person's name for typed signature
      const name = this.currentPersonNameValue
      if (!name) {
        alert('Unable to create typed signature')
        return
      }
      // Convert typed signature to image
      this.signatureData = await this.typeToImage(name)
    }

    // Update the overlay with the signature
    this.updateSignatureOverlay()

    this.closeSignatureModal()
    this.updateProgress()
  }

  async typeToImage(text) {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    // Measure text first to size canvas appropriately
    const fontSize = 48
    ctx.font = `${fontSize}px "Dancing Script", cursive`
    const metrics = ctx.measureText(text)

    // Size canvas to fit text with small padding
    const padding = 10
    canvas.width = Math.ceil(metrics.width) + padding * 2
    canvas.height = fontSize + padding * 2

    // White background
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Draw text (need to set font again after resize)
    ctx.font = `${fontSize}px "Dancing Script", cursive`
    ctx.fillStyle = 'black'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'top'
    ctx.fillText(text, padding, padding)

    return canvas.toDataURL('image/png')
  }

  updateSignatureOverlay() {
    // Find the signature overlay for the current person/agent
    let signatureSelector, dateSelector, nameSelector

    if (this.isAgentValue) {
      // For agents, find by signerType
      signatureSelector = `.signature-overlay[data-signer-type="agent"][data-field-type="signature"]`
      dateSelector = `.signature-overlay[data-signer-type="agent"][data-field-type="date"]`
      nameSelector = `.signature-overlay[data-signer-type="agent"][data-field-type="name"]`
    } else {
      // For tenants, find by personId
      signatureSelector = `.signature-overlay[data-person-id="${this.currentFieldPersonId}"][data-field-type="signature"]`
      dateSelector = `.signature-overlay[data-person-id="${this.currentFieldPersonId}"][data-field-type="date"]`
      nameSelector = null // Tenant names are pre-filled in PDF
    }

    const signatureOverlay = this.element.querySelector(signatureSelector)

    if (signatureOverlay && this.signatureData) {
      signatureOverlay.innerHTML = ''
      signatureOverlay.classList.remove('current-user')
      signatureOverlay.classList.add('signed')

      const img = document.createElement('img')
      img.src = this.signatureData
      img.alt = 'Your signature'
      signatureOverlay.appendChild(img)
    }

    // Find and show the date overlay
    const dateOverlay = this.element.querySelector(dateSelector)

    if (dateOverlay) {
      dateOverlay.style.display = ''
      dateOverlay.classList.add('signed')
      dateOverlay.innerHTML = `<span class="date-text">${this.formatDate(new Date().toISOString())}</span>`
    }

    // Update the name overlay (for agents) - keep editable until final submission
    // The name field stays interactive so the agent can still change it before submitting
  }

  updateProgress() {
    // Count unsigned fields for current user/agent
    let myFields
    if (this.isAgentValue) {
      // For agents, count agent signature fields
      myFields = this.positionsValue.filter(
        p => p.fieldType === 'signature' && p.signerType === 'agent'
      )
    } else {
      // For tenants, count by personId
      myFields = this.positionsValue.filter(
        p => p.fieldType === 'signature' && String(p.personId) === String(this.currentPersonIdValue)
      )
    }

    // Check if we have a signature
    const signed = this.signatureData !== null

    const remaining = signed ? 0 : myFields.length

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.style.display = remaining > 0 ? '' : 'none'
    }

    if (this.hasRemainingCountTarget) {
      this.remainingCountTarget.textContent = remaining
    }

    if (this.hasFinishButtonTarget) {
      this.finishButtonTarget.disabled = remaining > 0
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.style.display = remaining > 0 ? '' : 'none'
    }
  }

  scrollToNextField() {
    // Find the signature field specifically (not name field)
    const overlay = this.element.querySelector('.signature-overlay.current-user:not(.name-field)')
    if (overlay) {
      overlay.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  editAgentName(event, overlay, scaledFontSize) {
    event.stopPropagation()

    // Prevent re-entry if already editing
    if (overlay.querySelector('input')) return

    // Create inline input
    const currentName = this.agentName
    const fontSize = scaledFontSize || Math.round(10 * this.pdfScale)
    const input = document.createElement('input')
    input.type = 'text'
    input.value = currentName
    input.className = 'name-text'
    input.style.cssText = `width: 100%; height: 100%; border: none; background: transparent; text-align: left; font-size: ${fontSize}px; color: #1a1a1a; padding: 0 0 5px 3px; margin: 0; outline: none;`

    overlay.innerHTML = ''
    overlay.appendChild(input)
    input.focus()
    input.select()

    let saved = false
    const saveEdit = (nameToSave) => {
      if (saved) return
      saved = true
      const newName = nameToSave || input.value.trim() || currentName
      this.agentName = newName
      this.renderNameField(overlay, fontSize, newName)
    }

    input.addEventListener('blur', () => saveEdit())
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        saveEdit()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        saveEdit(currentName) // Revert to original
      }
    })
  }

  renderNameField(overlay, fontSize, name) {
    overlay.innerHTML = `<span class="name-text" style="font-size: ${fontSize}px">${this.escapeHtml(name)}</span>`
  }

  showIntentConfirmation() {
    if (!this.signatureData) return
    this.intentModalTarget.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
  }

  closeIntentModal() {
    this.intentModalTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
  }

  async submitSignature() {
    if (!this.signatureData) return

    // Show loading overlay
    this.loadingOverlayTarget.classList.remove('hidden')
    this.closeIntentModal()

    try {
      const body = {
        signature_data: this.signatureData,
        signature_method: this.signatureMethod
      }

      // Include agent name if in agent mode
      if (this.isAgentValue) {
        body.agent_name = this.agentName
      }

      const response = await fetch(this.submitUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (data.success && data.redirect_url) {
        window.location.href = data.redirect_url
      } else {
        throw new Error(data.error || 'Submission failed')
      }
    } catch (error) {
      console.error('Error submitting signature:', error)
      this.loadingOverlayTarget.classList.add('hidden')
      alert('Failed to submit signature. Please try again.')
    }
  }

  toggleMenu() {
    // Mobile menu toggle - can be expanded later
    console.log('Menu toggle clicked')
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Trim whitespace from signature canvas and return cropped data URL
  trimSignatureCanvas(canvas) {
    const ctx = canvas.getContext('2d')
    const width = canvas.width
    const height = canvas.height
    const imageData = ctx.getImageData(0, 0, width, height)
    const data = imageData.data

    // Find bounds of non-background pixels
    // Background is white rgb(255, 255, 255)
    let minX = width, minY = height, maxX = 0, maxY = 0
    const bgR = 255, bgG = 255, bgB = 255
    const tolerance = 10

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4
        const r = data[i], g = data[i + 1], b = data[i + 2]

        // Check if pixel is NOT background (with tolerance)
        if (Math.abs(r - bgR) > tolerance || Math.abs(g - bgG) > tolerance || Math.abs(b - bgB) > tolerance) {
          minX = Math.min(minX, x)
          minY = Math.min(minY, y)
          maxX = Math.max(maxX, x)
          maxY = Math.max(maxY, y)
        }
      }
    }

    // Add padding
    const padding = 10
    minX = Math.max(0, minX - padding)
    minY = Math.max(0, minY - padding)
    maxX = Math.min(width - 1, maxX + padding)
    maxY = Math.min(height - 1, maxY + padding)

    // If no signature found, return original
    if (minX >= maxX || minY >= maxY) {
      return canvas.toDataURL('image/png')
    }

    // Create cropped canvas
    const croppedWidth = maxX - minX
    const croppedHeight = maxY - minY
    const croppedCanvas = document.createElement('canvas')
    croppedCanvas.width = croppedWidth
    croppedCanvas.height = croppedHeight

    const croppedCtx = croppedCanvas.getContext('2d')
    // Fill with white background
    croppedCtx.fillStyle = 'white'
    croppedCtx.fillRect(0, 0, croppedWidth, croppedHeight)
    // Draw cropped signature
    croppedCtx.drawImage(canvas, minX, minY, croppedWidth, croppedHeight, 0, 0, croppedWidth, croppedHeight)

    return croppedCanvas.toDataURL('image/png')
  }

  loadPdfjsFromCdn() {
    return new Promise((resolve, reject) => {
      if (window.pdfjsLib) {
        resolve()
        return
      }
      const script = document.createElement('script')
      // Use legacy build that sets window.pdfjsLib
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
      script.onload = () => resolve()
      script.onerror = reject
      document.head.appendChild(script)
    })
  }
}
